import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Tag } from 'antd';
import { Link } from 'react-router-dom';
import * as MpProductsApi from '../../api/MpProducts';
import type { MpProduct } from '../../api/MpProducts';

type Props = {
  marketplaceAuthId: number;
  marketplace: string;
};

const MpProductsPanel = ({ marketplaceAuthId, marketplace }: Props) => {
  const [linkedFilter, setLinkedFilter] = useState<'unlinked' | 'linked' | 'all'>(
    'unlinked',
  );
  const [archivedFilter, setArchivedFilter] = useState<
    'active' | 'archived' | 'all'
  >('active');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<MpProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    linked: 0,
    unlinked: 0,
    archived: 0,
    active: 0,
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [linkArticle, setLinkArticle] = useState<Record<number, string>>({});
  const [goodsHints, setGoodsHints] = useState<
    { article: string; name: string }[]
  >([]);

  const reload = useCallback(async () => {
    setPending(true);
    setError('');
    try {
      const [listRes, statsRes] = await Promise.all([
        MpProductsApi.list({
          marketplace_auth_id: marketplaceAuthId,
          linked: linkedFilter,
          archived: archivedFilter,
          search: search.trim() || undefined,
          limit: 200,
        }),
        MpProductsApi.stats(marketplaceAuthId),
      ]);
      setItems(listRes.items);
      setTotal(listRes.total);
      setStats({
        total: statsRes.total,
        linked: statsRes.linked,
        unlinked: statsRes.unlinked,
        archived: statsRes.archived ?? 0,
        active: statsRes.active ?? statsRes.total - (statsRes.archived ?? 0),
      });
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setPending(false);
    }
  }, [marketplaceAuthId, linkedFilter, archivedFilter, search]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSearchGoods = async (q: string) => {
    if (q.trim().length < 2) {
      setGoodsHints([]);
      return;
    }
    try {
      setGoodsHints(await MpProductsApi.searchGoods(q));
    } catch {
      setGoodsHints([]);
    }
  };

  return (
    <div id="mp-products" style={{ marginTop: 8, paddingTop: 8 }}>
      <h5 style={{ marginBottom: 4 }}>Товары каталога МП</h5>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Карточки {marketplace} без привязки к 1С не уходят в отправку остатков.
        «Удалён на МП» — товар пропал из каталога при последнем sync.
      </p>

      <Row className="mb-3 g-2 align-items-end">
        <Col md={2}>
          <Form.Label>Связь с 1С</Form.Label>
          <Form.Select
            value={linkedFilter}
            onChange={(e) => setLinkedFilter(e.target.value as any)}
          >
            <option value="unlinked">Без связи</option>
            <option value="linked">Связанные</option>
            <option value="all">Все</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label>На МП</Form.Label>
          <Form.Select
            value={archivedFilter}
            onChange={(e) => setArchivedFilter(e.target.value as any)}
          >
            <option value="active">Активные</option>
            <option value="archived">Удалённые</option>
            <option value="all">Все</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Поиск</Form.Label>
          <Form.Control
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="sku / barcode / title"
          />
        </Col>
        <Col md={5} className="d-flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            disabled={pending}
            onClick={async () => {
              await MpProductsApi.enqueueSync(marketplaceAuthId);
              alert('Синхронизация каталога поставлена в очередь');
            }}
          >
            Sync каталог
          </Button>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={async () => {
              await MpProductsApi.enqueueSeed(marketplaceAuthId);
              alert('Seed связей из alias/barcode поставлен в очередь');
            }}
          >
            Seed связи
          </Button>
          <Button
            variant="outline-secondary"
            disabled={pending}
            onClick={async () => {
              await MpProductsApi.enqueueAutoMatch(marketplaceAuthId);
              alert('Auto-match поставлен в очередь');
            }}
          >
            Auto-match
          </Button>
          <Button variant="primary" disabled={pending} onClick={() => reload()}>
            Обновить
          </Button>
        </Col>
      </Row>

      <p>
        Всего: {stats.total} · активных: {stats.active} · удалённых:{' '}
        {stats.archived} · связано: {stats.linked} · без связи: {stats.unlinked}{' '}
        · в таблице: {total}
        {pending ? ' · загрузка…' : ''}
      </p>
      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>seller_sku</th>
            <th>barcode</th>
            <th>title</th>
            <th>На МП</th>
            <th>article 1С</th>
            <th>действия</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr
              key={p.id}
              style={
                p.is_archived
                  ? { opacity: 0.7, background: '#fafafa' }
                  : undefined
              }
            >
              <td>{p.seller_sku}</td>
              <td>{p.barcode}</td>
              <td>{p.title}</td>
              <td>
                {p.is_archived ? (
                  <Tag>Удалённый</Tag>
                ) : (
                  <Tag color="success">Активный</Tag>
                )}
              </td>
              <td>
                {p.article ? (
                  <Link
                    to={`/settings/goods/${encodeURIComponent(p.article)}`}
                  >
                    {p.article}
                  </Link>
                ) : (
                  '—'
                )}
              </td>
              <td style={{ minWidth: 260 }}>
                {p.article ? (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={async () => {
                      await MpProductsApi.unlink(p.id);
                      await reload();
                    }}
                  >
                    Отвязать
                  </Button>
                ) : (
                  <div className="d-flex gap-1">
                    <Form.Control
                      size="sm"
                      placeholder="артикул 1С"
                      value={linkArticle[p.id] || ''}
                      list={`goods-hints-${p.id}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLinkArticle((prev) => ({ ...prev, [p.id]: v }));
                        void onSearchGoods(v);
                      }}
                    />
                    <datalist id={`goods-hints-${p.id}`}>
                      {goodsHints.map((g) => (
                        <option key={g.article} value={g.article}>
                          {g.name}
                        </option>
                      ))}
                    </datalist>
                    <Button
                      size="sm"
                      onClick={async () => {
                        const article = (linkArticle[p.id] || '').trim();
                        if (!article) return;
                        await MpProductsApi.link(p.id, article);
                        await reload();
                      }}
                    >
                      Привязать
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {!pending && items.length === 0 && (
            <tr>
              <td colSpan={6}>
                {stats.total === 0
                  ? 'Каталог пуст — нажмите «Sync каталог»'
                  : 'Нет строк по текущему фильтру'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default MpProductsPanel;
